import { useEffect, useState } from "react";
import { FolderPlus, Images, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { AlbumDetail } from "@/components/AlbumDetail";
import { api, formatApiError } from "@/lib/api";

const SECTIONS = [
  { tipo: "historico", titulo: "Acervo Histórico", desc: "Memória do IMJ: fotos, matérias, documentos, certificados e apresentações recuperados pelo responsável." },
  { tipo: "momentos", titulo: "Momentos no IMJ", desc: "Dia a dia do instituto: aulas, ensaios, apresentações, eventos, alunos, professores e bastidores." },
];
const EMPTY = { titulo: "", descricao: "", ano: "", ordem: 0 };

export default function Albums() {
  const [albums, setAlbums] = useState([]);
  const [dialog, setDialog] = useState(null); // { tipo, album? }
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/albums");
    setAlbums(data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const openDialog = (tipo, album) => {
    setDialog({ tipo, album: album || null });
    setForm(album ? { titulo: album.titulo, descricao: album.descricao || "", ano: album.ano || "", ordem: album.ordem ?? 0 } : EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, ordem: Number(form.ordem) || 0, tipo: dialog.album ? dialog.album.tipo : dialog.tipo };
      if (dialog.album) await api.put(`/albums/${dialog.album.id}`, payload);
      else await api.post("/albums", payload);
      toast.success(dialog.album ? "Álbum atualizado." : "Álbum criado como não publicado.");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (album) => {
    try {
      await api.post(`/albums/${album.id}/${album.published ? "unpublish" : "publish"}`);
      toast.success(album.published ? "Álbum despublicado." : "Álbum publicado no site.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/albums/${deleting.id}`);
      toast.success("Álbum excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-10" data-testid="albums-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Fotos &amp; Álbuns</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gerencie o Acervo Histórico e os Momentos no IMJ. Novos álbuns nascem não publicados — a publicação no site é sempre explícita.
        </p>
      </div>

      {SECTIONS.map((sec) => {
        const lista = albums.filter((a) => a.tipo === sec.tipo);
        return (
          <section key={sec.tipo} className="space-y-4" data-testid={`album-section-${sec.tipo}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
              <div>
                <h2 className="font-heading text-xl font-bold uppercase tracking-wide">{sec.titulo}</h2>
                <p className="text-xs text-muted-foreground">{sec.desc}</p>
              </div>
              <Button variant="outline" onClick={() => openDialog(sec.tipo, null)} data-testid={`album-create-${sec.tipo}`}>
                <FolderPlus size={16} className="mr-2" /> Novo álbum
              </Button>
            </div>
            {lista.length === 0 && (
              <p className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid={`album-empty-${sec.tipo}`}>
                Nenhum álbum neste espaço ainda.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {lista.map((a) => (
                <div key={a.id} className="border border-border bg-white" data-testid={`album-card-${a.id}`}>
                  <button type="button" className="block w-full" onClick={() => setDetail(a)} data-testid={`album-open-${a.id}`}>
                    {a.capa_url ? (
                      <img src={a.capa_url} alt={a.titulo} className="aspect-[16/10] w-full object-cover" />
                    ) : (
                      <div className="flex aspect-[16/10] w-full items-center justify-center bg-secondary text-muted-foreground">
                        <Images size={32} />
                      </div>
                    )}
                  </button>
                  <div className="space-y-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{a.titulo}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.foto_count} foto(s){a.ano ? ` · ${a.ano}` : ""}
                        </p>
                      </div>
                      <Badge variant={a.published ? "default" : "secondary"} className={a.published ? "bg-primary text-primary-foreground" : ""} data-testid={`album-status-${a.id}`}>
                        {a.published ? "Publicado" : "Não publicado"}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => setDetail(a)} data-testid={`album-manage-${a.id}`}>Fotos</Button>
                      <Button variant="outline" size="sm" onClick={() => openDialog(sec.tipo, a)} data-testid={`album-edit-${a.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => togglePublish(a)} data-testid={`album-publish-${a.id}`}>
                        {a.published ? "Despublicar" : "Publicar"}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleting(a)} data-testid={`album-delete-${a.id}`}>
                        <Trash2 size={13} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="album-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.album ? "Editar álbum" : "Novo álbum"} — {SECTIONS.find((s) => s.tipo === (dialog?.album?.tipo || dialog?.tipo))?.titulo}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Título do álbum</Label>
              <Input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex.: Festivais, Fundação do IMJ, Apresentações 2026..." data-testid="album-titulo-input" />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Textarea rows={2} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} data-testid="album-descricao-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ano/período (opcional)</Label>
                <Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} placeholder="Ex.: 2011 ou 2005–2010" data-testid="album-ano-input" />
              </div>
              <div className="space-y-2">
                <Label>Ordem</Label>
                <Input type="number" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} data-testid="album-ordem-input" />
              </div>
            </div>
            {!dialog?.album && (
              <p className="text-xs text-muted-foreground">O álbum será criado como <strong>não publicado</strong> — ele só aparece no site após você publicar explicitamente.</p>
            )}
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="album-save-button">
              {saving ? "Salvando..." : "Salvar álbum"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {detail && (
        <AlbumDetail album={detail} onClose={() => setDetail(null)} onChanged={load} />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir álbum"
        description={`Deseja excluir o álbum "${deleting?.titulo}"? As ${deleting?.foto_count ?? 0} foto(s) serão removidas da exibição (exclusão lógica, sem apagar arquivos do armazenamento). Esta ação não pode ser desfeita pelo painel.`}
        onConfirm={remove}
        testId="album-delete-confirm"
      />
    </div>
  );
}
